import { Box, DialogProps, Link } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Close, CloudUpload, Description } from '@material-ui/icons';
import { Button, StandardDialog, TextField } from '@roc/ui';
import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';

const EXTENSION_REGEX = /.[^.]+$/;

const useStyles = makeStyles(() => ({
  button: {
    minWidth: '10vw',
  },
  top: {
    marginBottom: 10,
  },
  dragArea: {
    border: '2px dotted #427ED1',
    width: '100%',
    backgroundColor: '#EAF4FA',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexDirection: 'column',
    padding: 20,
    marginBottom: 10,
    borderRadius: 5,
    cursor: 'pointer',
  },
  icon: {
    minWidth: '20px',
    minHeight: '20px',
    width: '3vw',
    height: '3vw',
    color: '#4B89DA',
  },
  fileContainer: {
    border: '1px solid grey',
    backgroundColor: '#EAF4FA',
    padding: '10px 20px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    margin: '10px 0',
    borderRadius: 5,
  },

  fileName: {
    marginLeft: 5,
    marginRight: 2,
    flexGrow: 1,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

export interface FileUpload {
  name: string;
  file: File;
  disabled?: boolean;
}

interface IFileUploadModalProps {
  title: string;
  open: boolean;
  top?: React.ReactNode;
  multiple?: boolean;
  disabled?: boolean;
  dialogClasses?: DialogProps['classes'];
  onClose: () => void;
  onUpload: (uploads: FileUpload[]) => void;
}

interface FileUploadItemProps {
  name: string;
  file: File;
  disabled?: boolean;
  onSaveName: (name: string) => void;
  onRemove: () => void;
}

export const FileUploadItem = (props: FileUploadItemProps) => {
  const classes = useStyles();
  const [isEditing, setIsEditing] = useState(false);
  const [newName, setNewName] = useState(() =>
    props.name.replace(EXTENSION_REGEX, '')
  );

  const onEditName = () => {
    setIsEditing(true);
  };

  const onSaveName = () => {
    const extension = props.name.match(EXTENSION_REGEX)?.[0] ?? '';
    props.onSaveName(newName + extension);
    setIsEditing(false);
  };

  const onRemove = () => {
    props.onRemove();
  };

  return (
    <Box className={classes.fileContainer}>
      <Description />
      {isEditing ? (
        <>
          <TextField
            testId="file-name"
            value={newName}
            fullWidth
            onChange={e => setNewName(e.target.value)}
          />
          <Button testId="save" variant="outlined" onClick={onSaveName}>
            Save
          </Button>
        </>
      ) : (
        <>
          <div className={classes.fileName}>{props.name}</div>
          {!props.disabled && <Button
            testId="save"
            size="small"
            variant="outlined"
            onClick={onEditName}
          >
            Rename
          </Button>}
        </>
      )}
      {!props.disabled && <Close onClick={onRemove} />}
    </Box>
  );
};

export const FileUploadModal = ({
  title,
  open,
  top,
  multiple,
  disabled,
  dialogClasses,
  onClose,
  onUpload,
}: IFileUploadModalProps) => {
  const [items, setItems] = useState<FileUpload[]>([]);

  const classes = useStyles();

  const uploadAction = () => {
    onUpload(items);
    closeAction();
  };

  const closeAction = () => {
    onClose();
    setItems([]);
  };

  const onDrop = (acceptedFiles: File[]) => {
    const newItems = acceptedFiles.map(file => ({
      name: file.name,
      file: file,
    }));
    setItems(prevItems => [...(multiple ? prevItems : []), ...newItems]);
  };

  const onRemove = (index: number) => {
    setItems(prevFiles => {
      const newFiles = [...prevFiles];
      newFiles.splice(index, 1);
      return newFiles;
    });
  };

  const onRename = (index: number, newName: string) => {
    setItems(prevItems => {
      const items = [...prevItems];
      items[index].name = newName;
      return items;
    });
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple,
    maxFiles: multiple ? 0 : 1,
  });

  return (
    <StandardDialog
      title={title || 'UPLOAD FILES'}
      open={open}
      classes={dialogClasses}
      handleClose={closeAction}
      maxWidth="md"
      dialogActions={
        <Box flex="1" display="flex" justifyContent="flex-end">
          <Button style={{ marginRight: 16 }}
            testId="cancel"
            variant="outlined"
            color="primary"
            onClick={closeAction}
          >
            Cancel
          </Button>
          <Button
            testId="upload"
            variant="contained"
            color="primary"
            className={classes.button}
            disabled={disabled || !items.length}
            onClick={uploadAction}
          >
            Upload
          </Button>
        </Box>
      }
      dialogContent={
        <>
          {top && <Box className={classes.top}>{top}</Box>}
          <Box {...getRootProps()} className={classes.dragArea}>
            <input {...getInputProps()} />
            <CloudUpload className={classes.icon} />
            {isDragActive ? (
              <p>Drop the file(s) here...</p>
            ) : (
              <>
                <p>Drag and drop here</p>
                <p>or browse</p>
              </>
            )}
          </Box>
          <Box>
            {items.map((item, i) => (
              <FileUploadItem
                key={item.file.name}
                name={item.name}
                file={item.file}
                onSaveName={newName => onRename(i, newName)}
                onRemove={() => onRemove(i)}
              />
            ))}
          </Box>
        </>
      }
    />
  );
};
